import { parseDisplayJobId } from "@/helpers/bookingHelpers";
import { ClientBooking } from "@/redux/apis/clientBooking/types";
import { Booking } from "@/redux/slices/booking/types";

/**
 * Adapts an operator job (`bookingApi` / `/v3/provider/{op}/job`) to the client
 * `ClientBooking` shape so the existing BookingHistory columns + `BookingAction`
 * render verbatim for the operator's client-record Booking History.
 *
 * The provider job list carries only the display `jobNumber` (no numeric id), but that number is
 * `J` + the numeric job id, so `parseDisplayJobId` recovers the id `BookingAction` needs to open the
 * booking on the client detail route. The `is_editable`/`is_cancellable` flags aren't in this payload,
 * so they stay false — the booking sheet resolves editability from the fetched detail instead.
 */
export const toClientBooking = (job: Booking): ClientBooking => ({
  jobId: parseDisplayJobId(job.jobNumber),
  displayJobId: job.jobNumber,
  status: job.status,
  statusReason: "",
  pickupTime: job.dates.pickup,
  pickupTimezone: job.dates.timezone,
  commencementDate: job.dates.pickup,
  completionDate: null,
  // Operator job cost is in cents; ClientBooking.total is rendered as dollars.
  total: job.cost?.total != null ? job.cost.total / 100 : null,
  estimatedPayment: null,
  vehicleTotal: null,
  passengerName: job.passenger.name,
  pickupLocation: job.stops?.[0]?.address ?? "",
  destination: job.stops && job.stops.length > 1 ? job.stops[job.stops.length - 1]?.address ?? "" : "",
  // Flight provenance for the pickup-time caption — the provider job hangs the flight off whichever
  // stop carries it (same lookup as the operator Bookings table), so the operator's view of a client's
  // history shows the caption identically.
  pickupDelayMinutes: job.stops?.find((stop) => stop.flight)?.flight?.pickupDelayMinutes ?? null,
  derivedArrival: job.stops?.find((stop) => stop.flight)?.flight?.pickupDerivedArrival ?? null,
  flags: {
    is_editable: false,
    // Operators/EAs can cancel a client's booking on-behalf (old-portal parity). The provider job list
    // carries the real cancellability as metadata.is_cancel_allowed (= Job::canCancel()), so gate the
    // Cancel action on it rather than hardcoding false; the backend re-checks on cancel.
    is_cancellable: job.metadata.isCancelAllowed,
    // Operators viewing a client's Booking History always see cost — pricing is only hidden from the
    // actual client. The provider job list has no per-row pricing flag, so default true here (the real
    // per-client state is resolved from the fetched booking detail when the sheet opens).
    is_pricing_available: true,
    // Gates the "Email Invoice" action for the operator's client-record Booking History — the old portal
    // showed it to operators viewing a client too. Sourced from the provider job list metadata
    // (JobEntityBase adds is_payment_invoice_generated).
    is_payment_invoice_generated: job.metadata.isPaymentInvoiceGenerated,
    is_ratable: false,
    is_special_event: false,
    is_asap: false,
    is_driver_on_way: false,
    is_driver_on_trip: false,
    is_passenger_dropped_off: false,
  },
});
